import React, { useState } from "react";
import { Link } from "components/Link";
import { Box, Button } from "@chakra-ui/react";
import { useSegment } from "tracking";
import { withErrorBoundary } from "react-error-boundary";
import { Logger } from "lib/logger";

export type BreadcrumbItem = {
  url: string;
  label: string;
};

export type BreadcrumbsProps = {
  breadcrumbs: BreadcrumbItem[] | undefined;
  hasActiveItem?: boolean;
  variant?: string;
  dataTestId?: string;
};

const BreadcrumbsComponent: React.FC<BreadcrumbsProps> = ({
  breadcrumbs = [],
  variant = "breadcrumb",
  hasActiveItem = false,
  dataTestId = "breadcrumbs"
}) => {
  const [showButton, setShowButton] = useState(true);
  const { track } = useSegment();
  const filteredItems = hasActiveItem
    ? breadcrumbs.slice(0, breadcrumbs.length - 1)
    : breadcrumbs;
  const activeItem = hasActiveItem ? breadcrumbs[breadcrumbs.length - 1] : null;

  const handleClick = () => {
    const innerElements = document.getElementsByClassName(
      "inner-breadcrumbs"
    ) as HTMLCollectionOf<HTMLElement>;
    for (const element of innerElements) {
      element.setAttribute("style", "display: unset;");
    }
    setShowButton(false);
    track({
      event: "Page Element Clicked",
      properties: {
        module_content: "Breadcrumbs Ellipsis"
      }
    });
  };

  return (
    <Box
      role="navigation"
      aria-label="Breadcrumb"
      data-testid={dataTestId}
      pt={0}
      mt={{ lg: "7px" }}
      px={{ base: 2, md: "80px", lg: 2 }}
      h={{ base: "auto", md: "36px" }}
      pl={{ lg: "0" }}
      mb={{ md: "16px", lg: "25px" }}
    >
      {filteredItems.map((item, i) => {
        if (i === 0) {
          return (
            <Link
              variant={variant}
              key={`${item.url}-${item.label}`}
              href={item.url === "/shop" ? "/shop/bestsellers" : item.url}
              title={item.label}
              onClick={() => {
                track({
                  event: "Page Element Clicked",
                  properties: {
                    module_content: "Breadcrumbs Link",
                    module_content_url: item.url
                  }
                });
              }}
              fontSize={1}
              lineHeight="36px"
            >
              {item.label}
            </Link>
          );
        }
        return (
          <Link
            className="inner-breadcrumbs"
            variant={variant}
            display={{ base: "none", md: "unset" }}
            key={`${item.url}-${item.label}`}
            href={item.url === "/shop" ? "/shop/bestsellers" : item.url}
            title={item.label}
            onClick={() => {
              track({
                event: "Page Element Clicked",
                properties: {
                  module_content: "Breadcrumbs Link",
                  module_content_url: item.url
                }
              });
            }}
            fontSize={1}
          >
            {item.label}
          </Link>
        );
      })}

      {showButton && (
        <Button
          variant={variant}
          display={{ md: "none" }}
          onClick={handleClick}
          id="elipsis-button"
          fontSize={1}
          minH="auto"
          h="36px"
        >
          ...
        </Button>
      )}

      {activeItem && (
        <Link
          variant="breadcrumbActive"
          key={`${activeItem.url}-${activeItem.label}`}
          href={activeItem.url}
          title={activeItem.label}
          fontSize={1}
        >
          {activeItem.label}
        </Link>
      )}
    </Box>
  );
};

export const Breadcrumbs = withErrorBoundary(BreadcrumbsComponent, {
  fallback: <div />,
  onError(error) {
    Logger.warn(`BreadcrumbsB failed to load. ${error}`);
  }
});
